/**
 * React Query hook for fetching the headerTree page layout (getPageLayout).
 *
 * This is the data source `SectionGrid` binds to for EE/HCTR (headerTree) pages —
 * it returns the deployed `sections[]` model (each section has its own `columnHeaderTree` +
 * flat `columns`). Rows come from the separate `getDecklines` query, joined by `page_section`.
 *
 * Follows the same pattern as the hooks in `usePage.ts`.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import { PageLayout } from '@services/api/deckManagement/graphql/schema/API';
import { getPageLayout } from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import { extractGraphqlOperationName } from '@utils/graphqlOperationName';
import { createQueryOrMutationKey, createStandardQueryOptions, StandardQueryOptions } from '@utils/reactQueryConfig.ts';
import { PageLayoutOverrides } from 'types/PageSidePanel.types';

/**
 * Parameters for {@link useGetPageLayout}.
 * @property pageId - The unique page identifier to fetch the layout for.
 * @property resolvePeriods - Whether the backend resolves scenario/period tokens to display text.
 *   Page-view rendering MUST pass `true` (false = tree-editor/authoring mode, where
 *   resolvedScenario/resolvedPeriod/scenarioDisplay come back null). Defaults to `true`.
 * @property options - Additional React Query options.
 */
export interface UseGetPageLayoutParams {
  pageId?: string;
  resolvePeriods?: boolean;
  /**
   * DEV_TEAM_ADMIN preview override: render as this deck cycle would resolve it (getPageLayout
   * `deck_cycle`). Omitted = the deck's own cycle.
   */
  deckCycle?: string;
  /** Preview override: pin the reporting/plan year (getPageLayout `publish_year`). */
  publishYear?: string;
  /** Preview override: pin the reporting anchor date, ISO-8601 (getPageLayout `as_of_date`). */
  asOfDate?: string;
  options?: StandardQueryOptions;
}

// Query keys for PageLayout caching. Keyed by pageId + resolvePeriods, since the resolved
// (page-view) and unresolved (tree-editor) layouts are distinct cache entries for the same page.
// Admin preview overrides (deckCycle/publishYear/asOfDate) also key the cache so each previewed
// cycle/year/date is its own entry and never bleeds into the deck's own resolution.
export const pageLayoutQueryKeys = {
  all: createQueryOrMutationKey('pageLayout'),
  pageLayout: (pageId: string, resolvePeriods: boolean, overrides?: PageLayoutOverrides) =>
    createQueryOrMutationKey(
      ...pageLayoutQueryKeys.all,
      pageId,
      String(resolvePeriods),
      overrides?.deckCycle ?? '',
      overrides?.publishYear ?? '',
      overrides?.asOfDate ?? '',
    ),
};

/**
 * Hook to fetch the headerTree `PageLayout` for a page.
 * @param params - {@link UseGetPageLayoutParams}
 */
export const useGetPageLayout = ({
  pageId,
  resolvePeriods = true,
  deckCycle,
  publishYear,
  asOfDate,
  options,
}: UseGetPageLayoutParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageLayoutQueryKeys.pageLayout(pageId || '', resolvePeriods, { deckCycle, publishYear, asOfDate }),
    queryFn: async (): Promise<PageLayout> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      const response = await deckManagementClient.graphql({
        query: getPageLayout,
        variables: {
          page_id: pageId,
          resolve_periods: resolvePeriods,
          // Only sent when pinned; omitting them keeps the request identical to the default path.
          ...(deckCycle ? { deck_cycle: deckCycle } : {}),
          ...(publishYear ? { publish_year: publishYear } : {}),
          ...(asOfDate ? { as_of_date: asOfDate } : {}),
        },
      });

      if ('data' in response && response.data?.getPageLayout) {
        return response.data.getPageLayout as PageLayout;
      }

      throw new Error('Failed to fetch page layout or layout not found');
    },
    ...createStandardQueryOptions<PageLayout>({
      ...options,
      enabled: Boolean(pageId) && (options?.enabled ?? true),
      rumOp: extractGraphqlOperationName(getPageLayout),
    }),
  });
};
